// src/contexts/RoleContext.jsx
import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import api from '../services/api';

const RoleContext = createContext({
  roles: [],
  currentRole: 'auto',
  loading: true,
  setRole: () => {},
  refreshRoles: () => {},
});

export const useRole = () => useContext(RoleContext);

export const RoleProvider = ({ children }) => {
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentRole, setCurrentRole] = useState(
    () => localStorage.getItem('roleContext') || 'auto'
  );

  console.log('🔄 RoleProvider render - currentRole:', currentRole, 'roles:', roles);

  const fetchRoles = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        setRoles([]);
        setLoading(false);
        return;
      }

      setLoading(true);
      const { data } = await api.get('/me/context');

      let newRoles = Array.isArray(data?.roles) ? [...data.roles] : [];

      // ✅ NOVO: adicionar papel "responsavel" se backend retornar isResponsavel === true
      if (data?.isResponsavel === true && !newRoles.includes('responsavel')) {
        console.log('🧩 RoleContext: adicionando papel "responsavel" baseado em isResponsavel=true');
        newRoles.push('responsavel');
      }

      setRoles(newRoles);
    } catch (err) {
      console.warn('RoleContext: falha ao obter /me/context:', err?.response?.status || err?.message);
      setRoles([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRoles();

    const onAuthChange = () => fetchRoles();
    const onRoleReset = () => {
      console.log('🔄 RoleContext: Detectou reset de papel, voltando para auto');
      setCurrentRole('auto');
    };

    window.addEventListener('storage', onAuthChange);
    window.addEventListener('auth:changed', onAuthChange);
    window.addEventListener('auth:roleReset', onRoleReset);

    return () => {
      window.removeEventListener('storage', onAuthChange);
      window.removeEventListener('auth:changed', onAuthChange);
      window.removeEventListener('auth:roleReset', onRoleReset);
    };
  }, []);

  useEffect(() => {
    const syncRole = () => {
      const stored = localStorage.getItem('roleContext') || 'auto';
      if (stored !== currentRole) {
        console.log(`🔄 RoleContext: Sincronizando papel ${currentRole} → ${stored}`);
        setCurrentRole(stored);
      }
    };

    window.addEventListener('storage', syncRole);
    return () => window.removeEventListener('storage', syncRole);
  }, [currentRole]);

  const setRole = (role) => {
    console.log(`🎯 RoleContext: Mudando papel ${currentRole} → ${role}`);
    setCurrentRole(role);
    try {
      localStorage.setItem('roleContext', role);
    } catch {}
  };

  const value = useMemo(
    () => ({
      roles,
      currentRole,
      loading,
      setRole,
      refreshRoles: fetchRoles,
    }),
    [roles, currentRole, loading, setRole]
  );

  return <RoleContext.Provider value={value}>{children}</RoleContext.Provider>;
};

RoleProvider.propTypes = {
  children: PropTypes.node.isRequired,
};
